import { cn } from '../cn.js'

export interface StepIndicatorProps {
  /** 1-based. */
  current: number
  steps: readonly string[]
}

/**
 * Visible progress measurably improves completion on multi-step forms
 * (PLAN.md §11.6) — and it is the cheapest reassurance the funnel offers.
 */
export function StepIndicator({ current, steps }: StepIndicatorProps) {
  return (
    <nav aria-label="Progress">
      <p className="text-sm font-medium text-muted">
        Step {current} of {steps.length}
        {steps[current - 1] && <span className="text-ink"> · {steps[current - 1]}</span>}
      </p>

      <ol className="mt-2 flex gap-1.5" role="list">
        {steps.map((label, i) => {
          const n = i + 1
          const done = n < current
          const active = n === current
          return (
            <li
              key={label}
              className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-alt"
              // Announcing every segment would be noise; the sentence above
              // already carries the state for assistive tech.
              aria-hidden="true"
            >
              <span
                className={cn(
                  'block h-full rounded-full transition-all',
                  done && 'w-full bg-brand',
                  active && 'w-full bg-accent',
                  !done && !active && 'w-0',
                )}
              />
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
